import { useEffect, useState } from 'react'

// The music projection, as one screen sees it. P6-186 (M2, the window).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 IT CANNOT THROW, AND THE PAGE MUST RENDER WITHOUT IT
// ═══════════════════════════════════════════════════════════════════════════
//
// `src/portal/movie.js`'s rule, and it binds harder here: the music service runs
// on **vegapunk, a home line** (spec §1), so "the projection is a few minutes
// old" and "the box is off" are ordinary Tuesday states rather than incidents.
// `fetchMusic` therefore has NO rejecting branch — offline, 401, 500, 503, a
// body that is not JSON, no Functions at all under `vite dev` — every one of
// them resolves to a state that renders a sentence in the panel and nothing
// else. No caller needs a `catch`, and no caller can forget one.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 BUT NOT ALL OF THEM RESOLVE TO THE *SAME* SENTENCE — P6-200
// ═══════════════════════════════════════════════════════════════════════════
//
// They did, and it hid a production outage for as long as it existed. The site
// read music's projection out of the BOT's bucket, so `/api/music` answered 503
// `unpublished` forever — and the panel rendered *"Music is unavailable right
// now"*, which is exactly what it renders when the box is genuinely off.
// **A permanent misconfiguration wore the sentence for an ordinary Tuesday.**
//
// ⚠️ AND MAKING THE FUNCTION'S REASONS MORE PRECISE WOULD HAVE CHANGED NOTHING
// ON ITS OWN, which is the part worth writing down. This module discards the
// response body on `!response.ok` — deliberately, and it still does, because a
// closed table of OUR literals is the rule and a body is written elsewhere. So
// a new failure reason in `_lib/projections.js` is invisible to a member unless
// this file reads *something* off the wire and picks a different sentence. A
// fix that stopped at the Function would have been indistinguishable from doing
// nothing, on the surface where it actually matters.
//
// ✅ THE THING IT READS IS THE STATUS, AND ONLY TO SPLIT ONE WAY: a 500 from
// this endpoint means the deployment is wrong — `unbound`, `misbound`, or a
// `ConfigError` from `callerFromRequest`, which `configFailure` also answers
// with 500. **None of those come back on their own.** A 502 or a 503 is R2, the
// music service, or nobody having played anything, and all of those genuinely
// are *"right now"*. Two states, one honest distinction, and no server text on
// the page.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THREE STATES, AND `loading` IS NOT `unavailable`
// ═══════════════════════════════════════════════════════════════════════════
//
// For the first moment of every load NOTHING HAS FAILED. A panel that said
// "music is unavailable" there would be the portal reporting an outage that has
// not happened — and on a POLLED surface it would do it every time somebody
// opened the pill.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 WHAT THIS MODULE MUST NEVER GROW, AND WHY IT IS WRITTEN DOWN HERE
// ═══════════════════════════════════════════════════════════════════════════
//
// `music/utils/projection.py` deliberately publishes no `control`, no
// `last_commands` and no `now_playing.position_at_event`. Its reasoning is that
// **a null reads as a value**: `reachable_at: null` says *"the tunnel is
// unreachable"* when the truth is *"the tunnel does not exist yet"*, and the two
// render identically.
//
// A CLIENT is where that decision gets quietly undone, because a client is
// where somebody normalises a shape "so the component does not have to check".
// So, explicitly:
//
//   ⛔ no default for `control` — M4 builds the tunnel and M5 the buttons
//   ⛔ no default for `last_commands`
//   ⛔ no elapsed time, no progress, no scrub. `GuildPlayer` RECORDS NO PAUSE
//      TIME, so a paused track's position is not derivable from anything the
//      service stores. `started_at` and `state` are published and that is all
//      there is. M3 either teaches the player to record pause time or animates
//      from `started_at` and freezes on `paused` — either way it is M3's
//      decision to take, and a number invented here would pre-empt it with a
//      value that is wrong the moment somebody presses pause.
//
// `NOT_YET_PUBLISHED` below is that list as data, and
// `src/test/portal/music-window.test.jsx` asserts none of them ever appears in
// what this module returns — so a later "just fill in the shape" edit reds the
// build instead of shipping a control surface that claims to know something.

export const MUSIC_ENDPOINT = '/api/music'

export const LOADING = 'loading'
export const READY = 'ready'
export const UNAVAILABLE = 'unavailable'

/**
 * The deployment is wrong, and waiting will not fix it. P6-200.
 *
 * ⚠️ A SEPARATE STATE RATHER THAN A FLAG ON `unavailable`, because every
 * consumer that branches on the state should have to see it. `MusicPanel` is
 * the only one today and it renders a different sentence.
 */
export const MISCONFIGURED = 'misconfigured'

/**
 * The status that means *this deployment is misconfigured*, and nothing else.
 *
 * 🔴 A CONSTANT SO THE PANEL, THIS MODULE AND THE TEST CANNOT DISAGREE — the
 * same reason `POLL_MS` is one, and CLAUDE.md gotcha 21's shape: a guard and its
 * consumer are two implementations of one contract, and a literal in each is
 * how they drift.
 *
 * Everything the Function answers with a 500 is ours to fix and comes back on
 * no timescale at all: a missing binding, a binding on the wrong bucket, or a
 * missing config variable. Its 502s and 503s are R2, the music service, or
 * nobody having played anything — every one of which is genuinely *"right
 * now"*, and is what the other sentence is for.
 *
 * ⚠️ WHEN M6 ADDS THE 3b MUSIC BLOCK this endpoint grows a member key and can
 * fail `unaddressable`, which `MUSIC_FAILURE` has no entry for today. That is a
 * deployment fault too and belongs on this side of the line — add it to the
 * table with a 500 rather than letting it fall through to `unreadable`'s 502,
 * where it would read to a member as *"come back later"*.
 */
export const DEPLOYMENT_FAULT_STATUS = 500

/**
 * How often the panel asks, while it is on screen. MI7.
 *
 * *"Liveness by polling the projection, every 2s while the panel is visible.
 * State changes are rare and the object is tiny; smoothness is local."*
 *
 * ⚠️ IT IS A CONSTANT SO THE TEST AND THE HOOK CANNOT DISAGREE about the number,
 * which is CLAUDE.md gotcha 21's shape — a guard and its consumer are two
 * implementations of one contract, and a literal in each is how they drift.
 */
export const POLL_MS = 2_000

/**
 * The fields the publisher withholds on purpose. Never defaulted, never faked.
 *
 * 🔴 DATA RATHER THAN PROSE, because a comment saying "do not add these" is not
 * a thing a build can check and this repo has a long record of comments
 * outliving their reasons. The paths are dotted so the assertion can walk them.
 */
export const NOT_YET_PUBLISHED = Object.freeze([
  'control',
  'last_commands',
  'now_playing.position_at_event',
])

/** Nobody has answered yet. Renders nothing, because nothing has failed. */
export function loadingMusic() {
  return { state: LOADING, shared: null }
}

/** It did not load. Renders one sentence, and the rest of the page is intact. */
export function unavailableMusic() {
  return { state: UNAVAILABLE, shared: null }
}

/** It cannot load on this deployment until somebody changes the deployment.
 * Renders a different sentence, for the reason in the header. */
export function misconfiguredMusic() {
  return { state: MISCONFIGURED, shared: null }
}

/**
 * The Function's answer, checked at the seam.
 *
 * ⚠️ THE SHAPE IS CHECKED HERE TOO, and it is not a duplicate of the Function's
 * check. `functions/_lib/projections.js` asserts what came out of the BUCKET;
 * this asserts what came over the WIRE, and those are two different edges with a
 * proxy, a service worker and a browser cache in between.
 *
 * 🔴 IT COPIES NOTHING AND FILLS NOTHING IN. The envelope is passed straight
 * through, so a field the publisher withheld arrives at the component as
 * `undefined` — which is what makes it un-renderable rather than falsy. See the
 * header.
 */
export function buildMusic(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return unavailableMusic()
  const shared = payload.shared
  if (!shared || typeof shared !== 'object' || Array.isArray(shared)) return unavailableMusic()
  return { state: READY, shared }
}

/**
 * Ask the Function. Resolves to a music state, always — see the header.
 *
 * ⚠️ `credentials: 'same-origin'`: the session cookie is what makes this a 200
 * rather than a 401. `3a/` is readable by any signed-in member and by nobody
 * else, and the queue names the people in it.
 */
export async function fetchMusic(fetchImpl = globalThis.fetch) {
  if (typeof fetchImpl !== 'function') return unavailableMusic()
  try {
    const response = await fetchImpl(MUSIC_ENDPOINT, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    // 🔴 THE ONE THING READ OFF A FAILED RESPONSE, AND IT IS NOT THE BODY. The
    // status picks between two of OUR literals; nothing the Function wrote is
    // rendered. A body would be — see the header, and the closed-table rule
    // `_lib/projections.js` states for the same reason one layer down.
    if (!response.ok) {
      return response.status === DEPLOYMENT_FAULT_STATUS ? misconfiguredMusic() : unavailableMusic()
    }
    return buildMusic(await response.json())
  } catch {
    return unavailableMusic()
  }
}

/**
 * Is this tab actually being looked at?
 *
 * 🔴 `=== 'visible'`, NOT `!== 'hidden'`, AND THE DIRECTION IS THE DECISION.
 * The requirement is *a hidden tab must not poll*, so the comparison is written
 * to fail towards NOT polling: an environment that reports something this file
 * has never heard of gets silence rather than a two-second timer nobody asked
 * for. The cost is the honest one — in such an environment the panel would show
 * its first answer and never update — and it is bounded by `visibilitychange`,
 * which puts it right the moment the document says anything at all.
 */
export function tabIsVisible(doc = globalThis.document) {
  return doc?.visibilityState === 'visible'
}

/**
 * The same question as a value that re-renders. P6-211.
 *
 * 🔴 IT EXISTS SO THERE IS ONE ANSWER TO "IS ANYBODY WATCHING", NOT TWO.
 * `useMusic` below already tears its interval down on `visibilitychange`; the
 * band's glow has to stop its compositor loop on exactly the same signal, and a
 * second listener with its own notion of visible is a second thing that can
 * drift out of step with the first. So both read `tabIsVisible`, and its
 * fail-towards-silence direction — `=== 'visible'`, not `!== 'hidden'` — is
 * inherited rather than restated: an environment this file has never heard of
 * gets a STILL glow, which is the reduced-motion rendering and is correct.
 *
 * ⚠️ IT SYNCS ON MOUNT AS WELL AS ON THE EVENT. A panel opened while the tab is
 * already hidden — a background window somebody left on the music pill — would
 * otherwise start its loop and never hear an event to stop it, because
 * `visibilitychange` fires on the transition and not on arrival.
 */
export function useTabVisible() {
  const [visible, setVisible] = useState(tabIsVisible)

  useEffect(() => {
    const sync = () => setVisible(tabIsVisible())
    sync()
    const doc = globalThis.document
    doc?.addEventListener?.('visibilitychange', sync)
    return () => {
      doc?.removeEventListener?.('visibilitychange', sync)
    }
  }, [])

  return visible
}

/**
 * The music projection for one panel, kept live while somebody is looking.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 THE PANEL'S OWN MOUNT IS THE "IS IT OPEN" FLAG. THERE IS NO SECOND ONE.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `PortalHome` renders ONE panel at a time — the branch selector swaps them —
 * so this hook is called from inside `MusicPanel` rather than from the page,
 * and switching to another pill unmounts the component and runs this effect's
 * cleanup. *"Poll while the panel is visible"* is then a structural property
 * rather than a boolean somebody has to remember to pass, and there is no state
 * in which a stale `true` leaves a timer running behind a closed panel.
 *
 * ⚠️ THAT COVERS THE PANEL AND NOT THE TAB, which is the other half of the
 * requirement and the half a mount cannot answer. A member who opens the pill
 * and then switches tabs has a mounted component and is looking at something
 * else entirely, so `visibilitychange` is listened for and the interval is torn
 * down and rebuilt around it.
 *
 * ✅ AND COMING BACK LOADS IMMEDIATELY rather than waiting out an interval. The
 * first thing somebody does on returning to a tab is read it, so the answer they
 * read should not be the one from before they left.
 *
 * ⚠️ ONE REQUEST PER PANEL AND NO MODULE-LEVEL CACHE — the same trade
 * `session.js`, `directory.js` and `movie.js` all made, for the reason they
 * wrote down: a promise cache saves a request and buys shared mutable state
 * every test has to reset.
 */
export function useMusic() {
  const [music, setMusic] = useState(loadingMusic)

  useEffect(() => {
    let cancelled = false
    let timer = null

    // No `.catch`: `fetchMusic` has no rejecting branch, and a handler that can
    // never run reads as if it could.
    const load = () => {
      fetchMusic().then((answer) => {
        if (!cancelled) setMusic(answer)
      })
    }

    const stop = () => {
      if (timer !== null) {
        clearInterval(timer)
        timer = null
      }
    }

    // 🔴 GUARDED ON `timer === null` SO A REPEATED `visible` EVENT CANNOT STACK
    // A SECOND INTERVAL. Browsers fire `visibilitychange` on more than the two
    // transitions this cares about, and two live intervals would double the
    // request rate with nothing on the page looking different — the failure
    // would only ever be visible in somebody else's logs.
    const sync = () => {
      if (!tabIsVisible()) {
        stop()
        return
      }
      if (timer === null) {
        load()
        timer = setInterval(load, POLL_MS)
      }
    }

    sync()
    const doc = globalThis.document
    doc?.addEventListener?.('visibilitychange', sync)
    return () => {
      cancelled = true
      stop()
      doc?.removeEventListener?.('visibilitychange', sync)
    }
  }, [])

  return music
}

/**
 * The music projection, read ONCE. P6-182.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 A SECOND HOOK RATHER THAN A FLAG ON THE FIRST, AND THE REASON IS THE ONE
 *    `PortalHome` ALREADY WROTE DOWN AGAINST ITSELF
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * That file's note on why `useMusic` is called from inside `MusicPanel`: *"A
 * hook at this level would poll for a panel nobody has opened, on every portal
 * load."* Still true, and the activity feed does not weaken it — the feed needs
 * `3a/music.json` on every load, and it does NOT need it every two seconds.
 *
 * So the page reads it once, exactly as it reads `/api/movie` once, and the
 * panel keeps its own polling hook. `useMusic(…, { poll: false })` would have
 * been the same object with a parameter that can be passed wrongly, and MI7's
 * requirement — *poll while the panel is visible* — would stop being structural.
 *
 * ⚠️ WHAT IT COSTS, STATED RATHER THAN DISCOVERED: while the Music pill is
 * open there are TWO readers of `/api/music` — this one, static, and the
 * panel's, polling. They can disagree, and the older answer is the feed's. That
 * is why the feed carries its own as-of stamp built from the blob it actually
 * read, rather than inheriting the panel's: a stamp that named a fresher
 * projection than the rows under it is §6's failure exactly.
 */
export function useMusicOnce() {
  const [music, setMusic] = useState(loadingMusic)

  useEffect(() => {
    let cancelled = false
    // No `.catch`: `fetchMusic` has no rejecting branch, and a handler that can
    // never run reads as if it could.
    fetchMusic().then((answer) => {
      if (!cancelled) setMusic(answer)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return music
}

// ═══════════════════════════════════════════════════════════════════════════
// READING THE BLOB — one place, so two components cannot disagree
// ═══════════════════════════════════════════════════════════════════════════

/** Every row of a published list, defensively. `[]` for anything else. */
function rowsOf(value) {
  return Array.isArray(value) ? value.filter((row) => row && typeof row === 'object') : []
}

/**
 * What is playing, or `null`.
 *
 * `null` is a real, published, designed answer — `capture_room` returns it when
 * nothing has played in this guild since boot, and the publisher's own note
 * says that *"is a real observation"* and not the same as the cog being
 * unreachable. The panel renders a sentence for it rather than an empty box.
 */
export function nowPlayingOf(shared) {
  const track = shared?.now_playing
  return track && typeof track === 'object' && !Array.isArray(track) ? track : null
}

/** The live queue, in the 1-based order `/queue` shows a human. */
export function queueOf(shared) {
  return rowsOf(shared?.queue)
}

/** The canon, newest first — capped at `RECENT_CANON` by the publisher. */
export function canonOf(shared) {
  return rowsOf(shared?.canon)
}

/**
 * How big the canon really is, or `null`.
 *
 * 🔴 THE PUBLISHER SENDS BOTH `canon` AND `canon_size` PRECISELY SO THE PAGE CAN
 * SAY "20 OF 212" RATHER THAN IMPLYING 20 IS ALL OF IT. `canon` is capped at
 * `RECENT_CANON`; its length is a property of the cap, not of the crew's canon,
 * and rendering it as a total would be a number that is wrong by however much
 * history exists. `null` when the blob predates the field — see the panel, which
 * then says nothing rather than falling back to the cap.
 */
export function canonSize(shared) {
  const size = shared?.canon_size
  return Number.isInteger(size) ? size : null
}

/** The charts block, or `null`. */
export function chartsOf(shared) {
  const charts = shared?.charts
  return charts && typeof charts === 'object' && !Array.isArray(charts) ? charts : null
}

/** One chart's rows. `which` is `top_played` or `most_skipped`. */
export function chartRows(shared, which) {
  return rowsOf(chartsOf(shared)?.[which])
}

/**
 * The window the charts cover, in words, or `null`.
 *
 * The publisher sends `CHART_WINDOW` — the string `"month"` — and a chart with
 * no period on it is a leaderboard of an unstated span, which is the same class
 * of error as an undated projection. A period this file has no wording for is
 * `null` and the heading loses its qualifier rather than printing a raw token.
 */
const PERIODS = Object.freeze({ week: 'this week', month: 'this month', year: 'this year' })

export function chartPeriod(shared) {
  const period = chartsOf(shared)?.period
  return typeof period === 'string' && Object.hasOwn(PERIODS, period) ? PERIODS[period] : null
}

/**
 * `3:42` from a published duration, or `null`.
 *
 * ⚠️ THIS IS A LENGTH, NOT A POSITION, AND THE DIFFERENCE IS THE WHOLE OF M3.
 * `duration_s` is a fact about the track and does not move; an elapsed time
 * would be a fact about right now, and the service stores nothing that can
 * produce one for a paused track. Formatting the first is not a step towards
 * the second.
 */
export function runtime(seconds) {
  if (!Number.isFinite(Number(seconds))) return null
  const total = Math.floor(Number(seconds))
  if (total < 0) return null
  const minutes = Math.floor(total / 60)
  const rest = total % 60
  return `${minutes}:${String(rest).padStart(2, '0')}`
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 WHAT A TRACK IS CALLED — AND WHY THE ANSWER CAN BE "WE DO NOT KNOW"
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `music/utils/identity.py` returns a THIRD OUTCOME on purpose: an identity
 * whose `resolved` flag is false, *"not an error path, it is a first-class
 * result"*, for anything it could not confidently split into an artist and a
 * title. The publisher carries that flag on every row, and `_track_fields`
 * sends `title: null` rather than a fallback label when the store has never seen
 * a key — its own words: *"inventing a title here would put a guess in a field a
 * human reads as a fact."*
 *
 * So there are three cases and the page must not flatten them:
 *
 *   resolved, titled     the normaliser's artist + title. Lower-cased — see
 *                        MusicPanel on why that is a CSS decision and not a
 *                        string one.
 *   unresolved, titled   the upload's own words, in the uploader's own casing.
 *                        Shown, because it is what somebody queued and they
 *                        will recognise it; marked, because it is not an
 *                        identity and does not count towards a chart.
 *   no title at all      the store has no row for this key. `null`, and the
 *                        panel says so in words rather than printing the key.
 */
export function trackTitle(track) {
  const title = track?.title
  return typeof title === 'string' && title.trim() !== '' ? title : null
}

export function trackArtist(track) {
  const artist = track?.artist
  return typeof artist === 'string' && artist.trim() !== '' ? artist : null
}

/** Did the normaliser name this one? `false` for anything that is not `true`,
 * so a blob that omits the flag is treated as unresolved rather than counted. */
export function trackResolved(track) {
  return track?.resolved === true
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 THE PLAYING TRACK'S ARTWORK — P6-202
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The publisher sends `now_playing.artwork_url`: a **full URL**, never an id.
 * That was the row's one real design decision and it is recorded on the other
 * side, in `music/utils/projection.py::artwork_url` — the short of it being
 * that the identity key is `artist::title` and there is no id to send, so the
 * only way to build a URL here would be to parse `source_url` for a YouTube
 * video id. That would put YouTube's URL shape in a **second repo, in a second
 * language**, which is P6-188's `8080` literal, and it would break silently the
 * day anything but YouTube plays (ADR-0004 D6 exists because sourcing may have
 * to change).
 *
 * ⚠️ SO THIS FILE KNOWS NOTHING ABOUT ANY IMAGE HOST, AND MUST NOT LEARN. No
 * allow-list, no size ladder, no `i.ytimg.com`. `movie.js::posterUrl` pins
 * `image.tmdb.org` because there is exactly one host a TMDb poster can come
 * from; a track's artwork can come from YouTube, SoundCloud, Bandcamp or an
 * extractor nobody has written yet, and an origin check here would be a source
 * coupling wearing a security control's clothes.
 *
 * 🔴 IT IS THE SECOND HALF OF A CONTRACT WITH TWO IMPLEMENTATIONS, AND
 * CLAUDE.md GOTCHA 21 IS WHY IT EXISTS AT ALL. The publisher applies the same
 * rule to what leaves the bucket; this applies it to what arrived over the
 * wire, with a Function, a service worker and a browser cache in between —
 * `buildMusic` above states the same reasoning for the envelope's shape.
 * `music-window.test.jsx` drives **the publisher's own table of inputs** through
 * this function rather than asserting each side alone, which is the half of
 * gotcha 21 that a matching pair of unit tests does not buy.
 *
 * 🔴 AND DOING THAT FOUND A REAL SEAM, WHICH IS RECORDED RATHER THAN PAPERED
 * OVER — the two parsers genuinely disagree about one input. Measured, both
 * ways, on 2026-09-05:
 *
 *     'https:///no-host.jpg'
 *       Python  urlsplit(...).netloc == ''            -> REFUSED
 *       JS      new URL(...).host    == 'no-host.jpg' -> accepted
 *
 * WHATWG's parser skips leading slashes for a special scheme and promotes the
 * first path segment to the host; Python's does not. **Neither is wrong — the
 * input is degenerate — and trying to make two parsers agree byte for byte is
 * the trap gotcha 21 describes, not the way out of it.**
 *
 * ✅ SO WHAT IS ASSERTED IS THE PROPERTY THAT CAN ACTUALLY HURT, AND ITS
 * DIRECTION: **on the SCHEME the two agree exactly**, and the publisher is the
 * stricter of the pair everywhere they differ. The publisher is the only writer
 * of this field, so a value it refuses cannot reach this function at all; and
 * the one it accepts and this one would too is a `https://` URL, which is the
 * whole point. `music-window.test.jsx` pins that direction, and the divergent
 * case by name, so a later edit that made this side the more permissive one
 * reds the build.
 *
 * ⚠️ `http:` IS REFUSED, WHICH IS NARROWER THAN `trips/EntryCard.jsx::photoUrl`
 * AND DELIBERATELY SO. That one renders a URL somebody typed into Discord and
 * accepts either scheme. This one renders a value from an automated extractor
 * onto a page served over HTTPS, where an `http:` image is mixed content and is
 * blocked by the browser before a byte moves — so accepting it would buy a
 * blocked request, a console error and an `onError` fallback that all arrive at
 * the same pixels as simply saying there is no artwork.
 */
export function artworkUrl(track) {
  const raw = track?.artwork_url
  if (typeof raw !== 'string' || raw.trim() === '') return null
  let parsed
  try {
    // 🔴 `new URL` WITH NO BASE, SO A SCHEME-RELATIVE `//host/x.jpg` THROWS
    // RATHER THAN INHERITING THE PAGE'S ORIGIN. Resolving it against
    // `location` would turn somebody else's CDN path into a same-origin
    // request for a file this site does not serve — a 404 dressed as artwork.
    parsed = new URL(raw)
  } catch {
    return null
  }
  return parsed.protocol === 'https:' ? raw : null
}

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE IDS IN `3a/music.json` WERE JSON **NUMBERS**, AND EVERY REAL DISCORD
//    SNOWFLAKE WAS DESTROYED BY THAT BEFORE THIS FILE EVER SAW IT
// ═══════════════════════════════════════════════════════════════════════════
//
// ✅ FIXED ON THE PUBLISHER SIDE BY #426 — `_ids` and `_id` now stringify, and
// `music/tests/test_projection.py` asserts the SERIALISED FORM (the id quoted
// in the JSON text) rather than a Python value, because Python's arbitrary
// precision means no value assertion on that side could ever have seen it.
//
// ⚠️ THE PARAGRAPH BELOW IS KEPT IN THE PAST TENSE RATHER THAN DELETED, AND THE
// TENSE IS THE WHOLE EDIT. This block said "ARE" for a day after it stopped
// being true — P6-182 found it while reading the same field for the feed — and
// a comment asserting a fixed defect is exactly what CLAUDE.md gotcha 3 calls
// *"a lie with a countdown on it"*. What it records is still worth keeping: the
// shape of the bug, and why the refusal below is the right response to it.
//
// 🔴 AND THE REFUSAL IS NOT DEAD CODE. R2 holds the last object the music
// service wrote, so a blob published BEFORE #426 is the live answer until that
// service next reconciles — and a number arriving here after that would mean a
// regression, which is a thing to refuse rather than to coerce. This module was
// designed to need no second deploy for the fix, and it did not get one.
//
// MEASURED 2026-09-04 by running the real `build_music_projection` over a real
// snapshot and reading the bytes it produced — not inferred from the source:
//
//     "requester_id": 1039191320375402506        <- now_playing and every queue row
//     "inducted_by": [1039191320375402507]       <- every canon entry
//     "guild_id": "1039191320375402500"          <- a STRING, in the same blob
//     3b `user_id`: "1039191320375402506"        <- a STRING, same module
//
// and then asking the browser what it makes of the first two:
//
//     JSON.parse('{"requester_id": 1039191320375402506}').requester_id
//       -> 1039191320375402500        THREE DIGITS GONE
//     Number.MAX_SAFE_INTEGER = 9007199254740991
//
// Discord snowflakes are ~10^17-10^18. **Every real one exceeds 2^53**, so the
// value is rounded by `JSON.parse` in the Worker and again in the browser, and
// the original digits are not recoverable at either end. `utils/projection_publisher.py`
// stringifies every id it publishes (`str(picker)`, `str(season_id)`); music's
// own 3b blob stringifies `user_id`; these two 3a fields are the exception.
//
// ⚠️ THE FAILURE SHAPE IS THE WORST ONE AVAILABLE: a fixture id small enough to
// be exact makes every test pass, and every PRODUCTION id is wrong. Looking a
// rounded id up in the directory does not miss safely either — it misses, or it
// lands on somebody else and attributes a track to the wrong member.
//
// ✅ SO THE SITE REFUSES TO JOIN ON ONE, and it refuses in a way that needs no
// second deploy: `joinableId` takes a STRING that looks like a snowflake and
// nothing else, so the moment the music service publishes `str(...)` — a
// one-line change in `music/utils/projection.py`, which is that lane's to make
// and not this one's to reach across for — the names appear and the note below
// stops being rendered. Nothing here has to be edited or removed. The panel's
// behaviour is derived from what actually arrived.
//
// ✅ THAT PREDICTION WAS TESTED BY THE EVENT AND HELD. #426 made the one-line
// change on the other side; this file needed no edit for the names to appear,
// and the only thing P6-182 changed here was the TENSE of the heading. A
// compromise whose retirement costs nothing is the one worth writing.
//
// 🔴 REPORTED, NOT WORKED AROUND. The Function could read `.text()` and parse
// the big integers losslessly, and that would be the site building a repair for
// somebody else's type — a permanent complication of the one door, invisible
// once fixed, and easy to get subtly wrong over arbitrary JSON.

/** A Discord id as a key segment may contain it. Same shape `projections.js`
 * enforces on the member key, for the same reason: decimal digits, nothing else. */
const SNOWFLAKE = /^[0-9]{1,32}$/

/**
 * An id this page may look up in the member directory, or `null`.
 *
 * 🔴 A NUMBER IS REFUSED RATHER THAN COERCED. `String(1039191320375402500)` is a
 * perfectly well-formed id belonging to nobody, or to the wrong person — see
 * above. The directory is keyed by string because `3a/members.json` is a JSON
 * object; that is not the problem, and `String()` is not the fix.
 */
export function joinableId(value) {
  return typeof value === 'string' && SNOWFLAKE.test(value) ? value : null
}

/** The member who queued this track, or `null` when we cannot say. */
export function requesterId(track) {
  return joinableId(track?.requester_id)
}

/** The members a canon entry is credited to. Empty when none is joinable. */
export function inductedBy(entry) {
  const ids = Array.isArray(entry?.inducted_by) ? entry.inducted_by : []
  return ids.map(joinableId).filter((id) => id !== null)
}

/**
 * Did this blob carry an id the page had to drop? P6-186's finding.
 *
 * Drives one sentence on the panel, so a member reads *"we cannot show who
 * queued these"* rather than seeing a designed field silently missing — and so
 * it is not mistaken for the member directory being down, which is a different
 * outage with its own note two hundred pixels away.
 *
 * ⚠️ IT ASKS WHETHER A **PRESENT** ID WAS UNUSABLE, not whether one is absent.
 * A canon entry the bot inducted on its own has an empty `inducted_by` and that
 * is a measured emptiness, not a dropped id.
 */
export function idsWereDropped(shared) {
  const unusable = (value) =>
    value !== null && value !== undefined && joinableId(value) === null
  const now = nowPlayingOf(shared)
  if (now && unusable(now.requester_id)) return true
  if (queueOf(shared).some((track) => unusable(track.requester_id))) return true
  return canonOf(shared).some((entry) =>
    (Array.isArray(entry.inducted_by) ? entry.inducted_by : []).some(unusable),
  )
}
